import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import midaniLogoArabic from 'assets/images/midani-logo-arabic.png';
import midaniLogoEnglish from 'assets/images/midani-logo-english.png';
import homeGreyIcon from 'assets/images/icons/home-grey.svg';
import doubleDownArrowIcon from 'assets/images/icons/double-down-arrow.svg';
import AlertModal from './common-components/AlertModal';
import { DEV_ONLY, isRTL, updateSavedChanges } from 'config/config';
import SwitchUrlConfirmContent from './SwitchUrlConfirmContent';

const Sidebar = ({ isCollapsed, toggleCollapsed }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);

  const [authorizedRoutes, setAuthorizedRoutes] = useState(auth.companyPermissions);
  const [showNavigateConfirmModal, setShowNavigateConfirmModal] = useState(false);
  const [toUrl, setToUrl] = useState(null);

  useEffect(() => {
    setAuthorizedRoutes(auth.companyPermissions);
  }, [auth.companyPermissions]);

  const navigateToUrl = (url) => {
    if (auth.hasUnsavedChanges && auth.unsavedChangedType) {
      setToUrl(url);
      setShowNavigateConfirmModal(true);
    } else {
      updateSavedChanges(false);
      navigate(url);
    }
  };

  const closeModal = () => {
    setShowNavigateConfirmModal(false);
    setToUrl(null);
  };

  return (
    <Fragment>
      <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
        {toUrl && showNavigateConfirmModal && (
          <AlertModal show={showNavigateConfirmModal} closeModal={closeModal} actionButtons={[]}>
            <SwitchUrlConfirmContent toUrl={toUrl} closeModal={closeModal} />
          </AlertModal>
        )}
        <div className='sidebar-top'>
          <img src={isRTL() ? midaniLogoArabic : midaniLogoEnglish} alt='logo' />
          <span className='title'>{t('MIDANI')}</span>
        </div>
        <div className='sidebar-bottom'>
          <div onClick={toggleCollapsed}>
            <img
              className={`sidebar-toggle rotate-${isCollapsed === isRTL() ? '90' : '270'}`}
              src={doubleDownArrowIcon}
            />
          </div>
        </div>
        {Object.values(authorizedRoutes).map((singleModule) => {
          return (
            <section key={singleModule.order}>
              {singleModule.subMenu ? (
                <>
                  <p>{t(singleModule.displayName)}</p>
                  <ul>
                    {Object.values(singleModule.subMenu).map((singleSubModule, index) => {
                      return (
                        <li
                          className={`${
                            location.pathname.split('/')[1] === singleSubModule.url ? 'active' : ''
                          }`}
                          key={singleModule.order + ' ' + index}
                        >
                          <a
                            href={`/${singleSubModule.url}`}
                            onClick={(e) => {
                              e.preventDefault();
                              navigateToUrl(singleSubModule.url);
                            }}
                          >
                            <img src={singleSubModule.icon} />
                            <span>{t(singleSubModule.displayName)}</span>
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </>
              ) : (
                <ul>
                  <li
                    className={`${
                      location.pathname.startsWith('/' + singleModule.url) ? 'active' : ''
                    }`}
                  >
                    <a
                      href={`/${singleModule.url}`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigateToUrl(singleModule.url);
                      }}
                    >
                      <img src={singleModule.icon} />
                      <span>{t(singleModule.displayName)}</span>
                    </a>
                  </li>
                </ul>
              )}
            </section>
          );
        })}
        {DEV_ONLY && (
          <section>
            <ul>
              <li>
                <a href='/icons' target='_blank'>
                  <img src={homeGreyIcon} />
                  <span>{t('Icons')}</span>
                </a>
              </li>
              <li>
                <a href='/buttons' target='_blank'>
                  <img src={homeGreyIcon} />
                  <span>{t('Buttons')}</span>
                </a>
              </li>
            </ul>
          </section>
        )}
      </div>
    </Fragment>
  );
};

Sidebar.propTypes = {
  isCollapsed: PropTypes.bool.isRequired,
  toggleCollapsed: PropTypes.func.isRequired,
};

export default Sidebar;
